import React, { useRef, useState } from "react";
import { FaFileCsv, FaFileExcel, FaSpinner, FaFileImport } from "react-icons/fa";
import { apiService } from "../Config/api.service";
import * as XLSX from "xlsx";
import ImportMappingModal from "./ImportMappingModal";
import ExportModal from "./ExportModal";
function ImportExportButtons({
    model_name,
    filename = "export",
    domain = [],
    importUrl,
    onImportSuccess,
    dropdown,
    accept = ".csv,.xls,.xlsx",
    exampleUrl = null
}) {
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef();
    const [fileData, setFileData] = useState(null);
    const [fileColumns, setFileColumns] = useState([]);
    const [showMapping, setShowMapping] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [serverFields, setServerFields] = useState([]);
    const [importErrors, setImportErrors] = useState(null);

    const getServerFields = async () => {
        try {
            const response = await apiService.getElement(`/fields/${model_name}`);
            const fields = response.data.fields;
            setServerFields(fields);
        } catch (err) {
            alert(err.message || "Erreur lors de la récupération des champs");
        }
    };

    const openExportModal = () => {
        getServerFields();
        setShowExportModal(true);
    };


    const handleExport = async (selectedFields, format) => {
        setLoading(true);
        try {
            await apiService.exportData({
                model_name,
                fields: selectedFields, 
                domain,
                format,
                filename
            });
        } catch (err) {
            alert(err.message || "Erreur lors de l'export");
        }
        setLoading(false);
    };

    const handleImportClick = () => {
        fileInputRef.current && fileInputRef.current.click();
        getServerFields();
    };

    const handleFileChange = async (e) => {
        if (e.target.files.length > 0) {
            setImporting(true);
            const file = e.target.files[0];
            try {
                let rows = [];
                if (file.name.endsWith('.csv')) {
                    const text = await file.text();
                    const lines = text.split('\n').filter(Boolean);
                    const columns = lines[0].split(';');
                    rows = lines.slice(1).map(line => {
                        const values = line.split(';');
                        return Object.fromEntries(columns.map((col, i) => [col.trim(), values[i]?.trim()]));
                    });
                    setFileColumns(columns);
                    setFileData(rows);
                } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
                    const data = await file.arrayBuffer();
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                    const columns = json[0];
                    rows = json.slice(1).map(row =>
                        Object.fromEntries(columns.map((col, i) => [col, row[i]]))
                    );
                    setFileColumns(columns);
                    setFileData(rows);
                }
                setShowMapping(true);
            } catch (err) {
                alert(err.message || "Erreur lors de la lecture du fichier");
            }
            setImporting(false);
            e.target.value = null;
        }
    };

    // const handleDownloadExample = () => {
    //     if (exampleUrl) {
    //         window.open(exampleUrl, "_blank");
    //     } else {
    //         alert("Aucun fichier exemple disponible.");
    //     }
    // };

    const buttonBase =
        "flex items-center gap-2 rounded-md transition-colors duration-150 px-4 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-blue-400";
    const buttonExport =
        "bg-green-50 border border-green-400 text-green-700 hover:bg-green-100 hover:border-green-600";
    const buttonImport =
        "bg-blue-50 border border-blue-400 text-blue-700 hover:bg-blue-100 hover:border-blue-600";

    return (
        <div className={dropdown ? "flex gap-2 w-full" : "flex gap-2"}>
            <button
                type="button"
                className={`${buttonBase} ${buttonImport} ${dropdown ? "text-sm w-full" : ""}`}
                onClick={handleImportClick}
                disabled={importing}
                aria-label="Importer"
            >
                {importing ? (
                    <FaSpinner className="animate-spin" />
                ) : (
                    <FaFileImport />
                )}
                Importer
            </button>
            <input
                type="file"
                ref={fileInputRef}
                accept={accept}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
            <button
                className={`${buttonBase} ${buttonExport} ${dropdown ? "text-sm w-full" : ""}`}
                disabled={loading}
                onClick={openExportModal}
                aria-label="Exporter"
            >
                {loading ? (
                    <FaSpinner className="animate-spin" />
                ) : (
                    <FaFileCsv />
                )}
                Exporter
            </button>

            {showExportModal && (
                <ExportModal
                    fields={serverFields}
                    onExport={handleExport}
                    onCancel={() => setShowExportModal(false)}
                    loading={loading}
                />
            )}

            {showMapping && (
                <ImportMappingModal
                    columns={fileColumns}
                    modelFields={serverFields}
                    onConfirm={async (mapping) => {
                        const mappedData = fileData.map(row => {
                            const obj = {};
                            Object.entries(mapping).forEach(([col, field]) => {
                                if (field) obj[field] = row[col];
                            });
                            return obj;
                        });
                        try {
                            const res = await apiService.saveElement(importUrl, {"records":mappedData});
                            if (res.data && res.data.result.error) {
                                setImportErrors(res.data.result.details || res.data.result.error);
                            } else {
                                setImportErrors(null);
                                onImportSuccess && onImportSuccess();
                                alert("Import réussi !");
                                setShowMapping(false);
                                setFileData(null);
                                setFileColumns([]);
                            }
                        } catch (err) {
                            setImportErrors(err.message || "Erreur lors de l'import");
                        }
                    }}
                    onTest={async (mapping) => {
                        const mappedData = fileData.map(row => {
                            const obj = {};
                            Object.entries(mapping).forEach(([col, field]) => {
                                if (field) obj[field] = row[col];
                            });
                            return obj;
                        });
                        try {
                            const res = await apiService.saveElement(importUrl, {"records":mappedData, "test": true});
                            if (res.data && res.data.result.error) {
                                setImportErrors(res.data.result.details || res.data.result.error);
                            } else {
                                setImportErrors(null);
                                onImportSuccess && onImportSuccess();
                                alert("test réussi !");
                            }
                        } catch (err) {
                            setImportErrors(err.message || "Erreur lors de l'import");
                        }
                    }}
                    onCancel={() => {
                        setShowMapping(false);
                        setFileData(null);
                        setFileColumns([]);
                        setImportErrors(null);
                    }}
                    errors={importErrors}

                />
            )}
        </div>
    );
}

export default ImportExportButtons;